import { Component } from '@theme/component';
import { WishlistUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, requestIdleCallback } from '@theme/utilities';
import { QuickOrderUIUtils } from '@theme/quick-order-ui-utils';

/**
 * @typedef {Object} WishlistPageRefs
 * @property {HTMLElement} emptyState - The empty state container
 * @property {HTMLElement} loadingState - The loading state container
 * @property {HTMLElement} productsContainer - The main products container
 * @property {HTMLElement} productGrid - The product grid list element
 * @property {HTMLElement} undoNotification - The undo notification
 * @property {HTMLElement} undoMessage - The undo message text
 */

/**
 * Wishlist page component that displays and manages wishlist products
 * @extends {Component<WishlistPageRefs>}
 */
class WishlistPage extends Component {
  constructor() {
    super();

    /** @type {string[]} */
    this.wishlist = [];

    /** @type {string} */
    this.currentLayout = this.dataset.layout || 'grid';

    /** @type {boolean} */
    this.showAddToCart = this.dataset.showAddToCart === 'true';

    /** @type {boolean} */
    this.showQuantity = this.dataset.showQuantity === 'true';

    /** @type {string} */
    this.sectionId = this.dataset.sectionId || '';

    /** @type {string | null} */
    this.undoProductHandle = null;

    /** @type {number | null} */
    this.undoTimeout = null;

    /** @type {boolean} */
    this.isLoading = false;

    /** @type {string} */
    this.lastWishlistHash = '';

    /** @type {number | null} */
    this.lastUpdateTime = null;

    this.debouncedUpdateProducts = debounce(() => {
      this.updateProducts();
    }, 300);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.wishlistUpdate, this.handleWishlistUpdate.bind(this));
    document.addEventListener('cart:add', this.handleCartAdd.bind(this));

    this.wishlist = this.getWishlist();
    this.applyLayout();

    const existingProducts = this.refs.productsContainer?.querySelectorAll('.product-grid__item').length || 0;
    const allProducts = document.querySelectorAll('.product-grid__item').length;

    if (this.wishlist.length === 0) {
      this.showEmptyState();
    } else if (existingProducts > 0 || allProducts > 0) {
      this.showProductsState();
      this.addRemoveButtons();

      if (allProducts > 0 && existingProducts === 0) {
        const productGrid = this.refs.productsContainer?.querySelector('.product-grid');
        if (productGrid) {
          this.refs.productGrid = /** @type {HTMLElement} */ (productGrid);
        }
      }
    } else {
      // Check if we have server-side rendered products
      const serverProducts = this.querySelectorAll('[data-product-handle]');
      if (serverProducts.length > 0) {
        this.showProductsState();
        this.addRemoveButtons();
      } else {
        setTimeout(() => {
          this.checkForExistingProducts();
        }, 100);
      }
    }
  }

  /**
   * Check for existing products after DOM is ready
   */
  checkForExistingProducts() {
    const existingProducts = this.refs.productsContainer?.querySelectorAll('.product-grid__item').length || 0;

    const allProducts = document.querySelectorAll('.product-grid__item').length;

    if (existingProducts > 0 || allProducts > 0) {
      this.showProductsState();
      this.addRemoveButtons();

      if (allProducts > 0) {
        const productGrid = this.refs.productsContainer?.querySelector('.product-grid');
        if (productGrid) {
          this.refs.productGrid = /** @type {HTMLElement} */ (productGrid);
        }
      }
    } else {
      const productGrid = this.refs.productsContainer?.querySelector('.product-grid');
      if (productGrid) {
        this.refs.productGrid = /** @type {HTMLElement} */ (productGrid);
      }

      if (this.wishlist.length === 0) {
        this.showEmptyState();
      } else {
        this.updateDisplay();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.undoTimeout) {
      clearTimeout(this.undoTimeout);
    }

    document.removeEventListener(ThemeEvents.wishlistUpdate, this.handleWishlistUpdate.bind(this));
    document.removeEventListener('cart:add', this.handleCartAdd.bind(this));
  }

  /**
   * Get wishlist from localStorage or server-side data
   * @returns {string[]}
   */
  getWishlist() {
    if (Theme.wishlist?.useServer && Theme.wishlist?.customerId) {
      const serverWishlist = this.getServerWishlist();
      if (serverWishlist.length > 0) {
        return serverWishlist;
      }
    }

    try {
      const localWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      return localWishlist;
    } catch {
      return [];
    }
  }

  /**
   * Get wishlist from server-side rendered data
   * @returns {string[]}
   */
  getServerWishlist() {
    const serverDataScript = document.getElementById('server-wishlist-data');

    if (serverDataScript) {
      try {
        const serverWishlist = JSON.parse(serverDataScript.textContent);
        return serverWishlist;
      } catch (error) {
        console.error('Error parsing server wishlist data:', error);
      }
    }

    const productItems = this.querySelectorAll('[data-product-handle]');
    const handles = Array.from(productItems).map((item) => /** @type {HTMLElement} */ (item).dataset.productHandle);
    const filteredHandles = handles.filter((handle) => handle !== undefined);

    if (filteredHandles.length > 0) {
      return filteredHandles;
    }

    return [];
  }

  /**
   * Save wishlist to localStorage and optionally to server
   * @param {string[]} wishlist
   */
  saveWishlist(wishlist) {
    localStorage.setItem('wishlist', JSON.stringify(wishlist));

    if (Theme.wishlist?.useServer && Theme.wishlist?.customerId && Theme.wishlist?.endpoint) {
      this.syncToServer(wishlist);
    }
  }

  /**
   * Sync wishlist to server
   * @param {string[]} wishlist
   */
  async syncToServer(wishlist) {
    try {
      const response = await fetch(Theme.wishlist.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-MagicFX-Auth': '5836747fcb372c47e1043b962285f52cd1a13c0074c38b2e9dbae77c50c7a603',
        },
        body: JSON.stringify({
          customerid: Theme.wishlist.customerId,
          wishlist,
        }),
      });

      if (!response.ok) {
        console.warn('Failed to sync wishlist to server:', response.status);
      }
    } catch (error) {
      console.warn('Error syncing wishlist to server:', error);
    }
  }

  /**
   * Handles cart add events from wishlist items
   * @param {CustomEvent} event
   */
  handleCartAdd(event) {
    if (
      event.detail?.data?.source === 'product-form-component' &&
      /** @type {HTMLElement} */ (event.target)?.closest('wishlist-page')
    ) {
      const quantity = event.detail?.data?.itemCount || 1;
      const productId = event.detail?.data?.productId;

      if (productId) {
        const productElement = this.querySelector(`[data-product-id="${productId}"]`);
        const productTitle = productElement?.querySelector('.product-card__title a')?.textContent?.trim() || 'Product';

        const message = `${quantity} × ${productTitle} added to cart`;
        QuickOrderUIUtils.createToast(message, 'success');
      }
    }
  }

  /**
   * Handles wishlist update events from other components
   * @param {CustomEvent} event
   */
  handleWishlistUpdate(event) {
    if (event.detail.source === this.id || event.detail.source === 'wishlist-page') {
      return;
    }

    const { wishlist } = event.detail;
    this.wishlist = wishlist;

    requestIdleCallback(() => {
      this.updateDisplay();
    });
  }

  /**
   * Update the display based on current wishlist state
   */
  updateDisplay() {
    if (this.wishlist.length === 0) {
      this.showEmptyState();
    } else {
      const currentWishlistHash = [...this.wishlist].sort().join(',');

      const existingProducts = this.querySelectorAll('[data-product-handle]');
      const hasServerRenderedProducts = existingProducts.length > 0;

      if (hasServerRenderedProducts) {
        this.updateServerRenderedProducts();
        this.showProductsState();
        this.addRemoveButtons();
      } else if (this.lastWishlistHash !== currentWishlistHash && !this.isLoading) {
        this.lastWishlistHash = currentWishlistHash;
        this.showLoadingState();
        this.debouncedUpdateProducts();
      } else if (!this.isLoading) {
        this.showProductsState();
      }
    }
  }

  /**
   * Update server-rendered products to hide removed items
   */
  updateServerRenderedProducts() {
    const productItems = this.querySelectorAll('[data-product-handle]');

    const productMap = new Map();
    productItems.forEach((item) => {
      const handle = /** @type {HTMLElement} */ (item).dataset.productHandle;
      if (handle) {
        productMap.set(handle, item);
      }
    });

    productItems.forEach((item) => {
      const handle = /** @type {HTMLElement} */ (item).dataset.productHandle;
      if (handle && !this.wishlist.includes(handle)) {
        /** @type {HTMLElement} */ (item).style.display = 'none';
      } else if (handle && this.wishlist.includes(handle)) {
        /** @type {HTMLElement} */ (item).style.display = '';
      }
    });

    const productGrid = this.refs.productGrid;
    if (productGrid && this.wishlist.length > 0) {
      /** @type {HTMLElement[]} */
      const orderedProducts = [];
      this.wishlist.forEach((handle) => {
        const productElement = productMap.get(handle);
        if (productElement && this.wishlist.includes(handle)) {
          orderedProducts.push(productElement);
        }
      });

      orderedProducts.forEach((productElement) => {
        productGrid.appendChild(productElement);
      });
    }
  }

  /**
   * Update the product recommendations with current wishlist items
   */
  updateProducts() {
    if (!this.refs.productGrid || this.wishlist.length === 0 || this.isLoading) {
      return;
    }

    const now = Date.now();
    if (this.lastUpdateTime && now - this.lastUpdateTime < 1000) {
      return;
    }
    this.lastUpdateTime = now;

    this.isLoading = true;

    const searchQuery = this.wishlist.map((handle) => `handle:${handle}`).join(' OR ');
    const url = `/search?q=${encodeURIComponent(searchQuery)}&section_id=wishlist-products`;

    this.loadWishlistProducts(url);
  }

  /**
   * Load wishlist products using section rendering
   * @param {string} url
   */
  async loadWishlistProducts(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const wishlistPageElement = tempDiv.querySelector('wishlist-page');

      if (wishlistPageElement?.innerHTML && wishlistPageElement.innerHTML.trim().length) {
        const newProductsContainer = wishlistPageElement.querySelector('.wishlist-page__products');

        if (newProductsContainer && this.refs.productsContainer) {
          const fetchedProductItems = Array.from(newProductsContainer.querySelectorAll('.product-grid__item'));

          const productMap = new Map();
          fetchedProductItems.forEach((item) => {
            const handle = /** @type {HTMLElement} */ (item).dataset.productHandle;
            if (handle) {
              productMap.set(handle, item);
            }
          });

          /** @type {HTMLElement[]} */
          const orderedProducts = [];
          this.wishlist.forEach((handle) => {
            const productElement = productMap.get(handle);
            if (productElement) {
              orderedProducts.push(productElement);
            }
          });

          const productGrid = newProductsContainer.querySelector('.product-grid');
          if (productGrid && orderedProducts.length > 0) {
            productGrid.innerHTML = '';

            orderedProducts.forEach((productElement) => {
              productGrid.appendChild(productElement);
            });
          }

          this.refs.productsContainer.innerHTML = newProductsContainer.innerHTML;

          const newProductGrid = /** @type {HTMLElement} */ (this.querySelector('[ref="productGrid"]'));
          if (newProductGrid) {
            this.refs.productGrid = newProductGrid;

            this.applyLayout();

            this.addRemoveButtons();
          }

          this.showProductsState();
        } else {
          this.showEmptyState();
        }
      } else {
        this.showEmptyState();
      }
    } catch (error) {
      console.error('Error loading wishlist products:', error);
      this.showEmptyState();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Add remove buttons to all product cards
   */
  addRemoveButtons() {
    if (!this.refs.productGrid) return;

    const productItems = this.refs.productGrid.querySelectorAll('.product-grid__item');

    productItems.forEach((item) => {
      const productCard = item.querySelector('product-card');
      if (!productCard) return;

      const handle = /** @type {HTMLElement} */ (item).dataset.productHandle;

      if (!handle || !this.wishlist.includes(handle)) return;

      if (productCard.querySelector('.wishlist-remove-button')) return;

      const removeButton = document.createElement('button');
      removeButton.className = 'wishlist-remove-button';
      removeButton.innerHTML = `
        <span class="visually-hidden">Remove from wishlist</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      `;
      removeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeFromWishlist(handle);
      });

      /** @type {HTMLElement} */ (productCard).style.position = 'relative';
      productCard.appendChild(removeButton);
    });
  }

  /**
   * Remove product from wishlist
   * @param {string} handle
   */
  removeFromWishlist(handle) {
    const newWishlist = this.wishlist.filter((h) => h !== handle);
    this.wishlist = newWishlist;
    this.saveWishlist(newWishlist);

    this.undoProductHandle = handle;
    this.showUndoNotification(handle);

    const messages = Theme.wishlist;
    const message = messages?.remove || 'Removed from wishlist';
    QuickOrderUIUtils.createToast(message, 'info');

    document.dispatchEvent(
      new WishlistUpdateEvent(newWishlist, this.id || 'wishlist-page', {
        isLoading: false,
      }),
    );

    this.updateDisplay();
  }

  /**
   * Show undo notification
   * @param {string} handle
   */
  showUndoNotification(handle) {
    if (!this.refs.undoNotification || !this.refs.undoMessage) return;

    const removedItem = document.querySelector(`[data-product-handle="${handle}"]`);
    const productTitle =
      removedItem?.querySelector('.product-title, [data-product-title]')?.textContent?.trim() || handle;

    this.refs.undoMessage.textContent = `${productTitle} removed from wishlist`;
    this.refs.undoNotification.hidden = false;

    if (this.undoTimeout) clearTimeout(this.undoTimeout);
    this.undoTimeout = setTimeout(() => {
      this.hideUndoNotification();
    }, 5000);
  }

  /**
   * Hide undo notification
   */
  hideUndoNotification() {
    if (this.refs.undoNotification) {
      this.refs.undoNotification.hidden = true;
    }
    this.undoProductHandle = null;
  }

  /**
   * Undo remove action
   */
  undoRemove() {
    if (!this.undoProductHandle) return;

    const newWishlist = [this.undoProductHandle, ...this.wishlist];
    this.wishlist = newWishlist;
    this.saveWishlist(newWishlist);

    document.dispatchEvent(new WishlistUpdateEvent(newWishlist, this.id || 'wishlist-page'));

    this.hideUndoNotification();
    this.updateDisplay();

    if (this.undoTimeout) {
      clearTimeout(this.undoTimeout);
    }
  }

  /**
   * Show loading state
   */
  showLoadingState() {
    if (this.refs.emptyState) this.refs.emptyState.hidden = true;
    if (this.refs.productsContainer) this.refs.productsContainer.hidden = true;
    if (this.refs.loadingState) this.refs.loadingState.hidden = false;
  }

  /**
   * Show empty state
   */
  showEmptyState() {
    if (this.refs.loadingState) {
      this.refs.loadingState.hidden = true;
    }
    if (this.refs.productsContainer) {
      this.refs.productsContainer.hidden = true;
    }
    if (this.refs.emptyState) {
      this.refs.emptyState.hidden = false;
    }
  }

  /**
   * Show products state
   */
  showProductsState() {
    if (this.refs.loadingState) {
      this.refs.loadingState.hidden = true;
    }
    if (this.refs.emptyState) {
      this.refs.emptyState.hidden = true;
    }
    if (this.refs.productsContainer) {
      this.refs.productsContainer.hidden = false;
    }
  }

  /**
   * Apply the merchant-set layout from theme settings
   */
  applyLayout() {
    const productGrid = this.refs.productGrid;
    if (!productGrid) return;

    productGrid.className = productGrid.className.replace(/product-grid--(grid|list)/g, '');

    productGrid.classList.add(`product-grid--${this.currentLayout}`);
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
